import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useRef,
} from 'react'
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  onIdTokenChanged,
  setPersistence,
  signOut,
  Unsubscribe,
} from 'firebase/auth'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import {AUTH_EXPIRED_EVENT, ensureFirebaseSession, recoverOrExpireSession, withTimeout} from './session'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const redirectToLogin = () => {
  if (window.location.pathname.includes('/auth')) {
    return
  }

  window.location.assign('/auth/login')
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const loggingOut = useRef(false)

  const saveAuth = useCallback((nextAuth: AuthModel | undefined) => {
    setAuth(nextAuth)
    if (nextAuth) {
      authHelper.setAuth(nextAuth)
    } else {
      authHelper.removeAuth()
    }
  }, [])

  const logout = useCallback(() => {
    if (loggingOut.current) {
      return
    }
    loggingOut.current = true
    saveAuth(undefined)
    setCurrentUser(undefined)
    signOut(getAuth()).catch(() => undefined)
    window.setTimeout(() => {
      loggingOut.current = false
    }, 500)
  }, [saveAuth])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {saveAuth, logout, setCurrentUser} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const logoutRef = useRef(logout)
  const didInit = useRef(false)
  logoutRef.current = logout

  useEffect(() => {
    const auth = getAuth()
    const unsubscribers: Unsubscribe[] = []
    let cancelled = false

    const expireSession = () => {
      logoutRef.current()
      redirectToLogin()
    }

    const hydrateUser = async (uid: string) => {
      const {data} = await withTimeout(getUserByToken(uid), 20000)
      if (!data) {
        throw new Error('User profile not found')
      }
      saveAuth({api_token: uid, refreshToken: auth.currentUser?.refreshToken})
      setCurrentUser(data)
    }

    const start = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence)
      } catch (error) {
        console.error('Failed to set Firebase persistence', error)
      }

      if (cancelled) {
        return
      }

      unsubscribers.push(
        onAuthStateChanged(auth, async (firebaseUser) => {
          try {
            if (!firebaseUser) {
              saveAuth(undefined)
              setCurrentUser(undefined)
              return
            }

            await ensureFirebaseSession()
            await hydrateUser(firebaseUser.uid)
          } catch (error) {
            console.error(error)
            expireSession()
          } finally {
            didInit.current = true
            setShowSplashScreen(false)
          }
        })
      )

      unsubscribers.push(
        onIdTokenChanged(auth, (firebaseUser) => {
          if (didInit.current && !firebaseUser) {
            expireSession()
          }
        })
      )
    }

    start()

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !didInit.current) {
        return
      }

      ensureFirebaseSession().catch(() => expireSession())
    }

    const onAuthExpired = () => expireSession()

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      recoverOrExpireSession(event.reason).then((expired) => {
        if (expired) {
          event.preventDefault()
        }
      })
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    return () => {
      cancelled = true
      unsubscribers.forEach((unsubscribe) => unsubscribe())
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
    }
  }, [saveAuth, setCurrentUser])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
